<template>
    <div class="ppv2-space-y-2" :class="{ isNotrequired: !required }">
        <!-- Label -->
        <label v-if="label" :for="inputId" class="ppv2-block ppv2-text-sm poppinsMedium">
            {{ label }}<span v-if="required" class="text-purple">*</span>
        </label>

        <!-- Description -->
        <p v-if="description" class="ppv2-text-xs ppv2-text-gray-500">
            {{ description }}
        </p>

        <!-- Input Container -->
        <div class="ppv2-relative">
            <!-- Left Icon -->
            <div
                v-if="leftIcon || $slots.leftIcon"
                class="ppv2-absolute ppv2-inset-y-0 ppv2-left-0 ppv2-pl-3 ppv2-flex ppv2-items-center"
            >
                <slot name="leftIcon">
                    <component v-if="leftIcon" :is="leftIcon" class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" />
                </slot>
            </div>

            <!-- Input Field -->
            <input
                :id="inputId"
                :type="type"
                :value="computedValue"
                :placeholder="placeholder"
                :disabled="disabled"
                :readonly="readonly"
                :class="inputClasses"
                @input="onInput"
                @blur="$emit('blur', $event)"
                @focus="$emit('focus', $event)"
                @keyup.enter="$emit('enter', $event)"
            />

            <!-- Right Icon or Suffix -->
            <div
                v-if="rightIcon || suffix || $slots.rightIcon"
                class="ppv2-absolute ppv2-inset-y-0 ppv2-right-0 ppv2-pr-3 ppv2-flex ppv2-items-center"
            >
                <slot name="rightIcon">
                    <span v-if="suffix" class="ppv2-text-gray-500 ppv2-text-sm">{{ suffix }}</span>
                    <component v-else-if="rightIcon" :is="rightIcon" class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" />
                </slot>
            </div>
        </div>

        <!-- Error Message -->
        <p v-if="error" class="ppv2-text-xs ppv2-text-red-600">
            {{ error }}
        </p>

        <!-- Help Text -->
        <p v-if="helpText" class="ppv2-text-xs ppv2-text-gray-500">
            {{ helpText }}
        </p>
    </div>
</template>

<script>
export default {
    name: 'BaseDate',
    props: {
        value: {
            type: [String, Date],
            default: '',
        },
        label: String,
        description: String,
        placeholder: String,
        type: {
            type: String,
            default: 'date',
        },
        required: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        error: String,
        helpText: String,
        leftIcon: String,
        rightIcon: String,
        suffix: String,
        size: {
            type: String,
            default: 'md',
            validator: (value) => ['sm', 'md', 'lg'].includes(value),
        },
    },
    data() {
        return {
            internalInputId: `input-${Math.random().toString(36).substr(2, 9)}`,
        }
    },
    computed: {
        inputId() {
            return this.internalInputId
        },
        computedValue() {
            return this.value != null ? String(this.value) : ''
        },
        inputClasses() {
            const baseClasses = [
                'ppv2-h-[59px]',
                'ppv2-block',
                'ppv2-w-full',
                'ppv2-border',
                'ppv2-border-gray-300',
                'ppv2-rounded-lg',
                'ppv2-placeholder-gray-400',
                'ppv2-focus:outline-none',
                'ppv2-focus:ring-2',
                'ppv2-focus:ring-blue-500',
                'ppv2-focus:border-blue-500',
                'ppv2-transition-colors',
                'border',
            ]

            if (this.leftIcon || this.$slots.leftIcon) {
                baseClasses.push('ppv2-pl-10')
            } else {
                baseClasses.push('ppv2-pl-3')
            }

            if (this.rightIcon || this.suffix || this.$slots.rightIcon) {
                baseClasses.push('ppv2-pr-10')
            } else {
                baseClasses.push('ppv2-pr-3')
            }

            switch (this.size) {
                case 'sm':
                    baseClasses.push('ppv2-py-1', 'ppv2-text-sm')
                    break
                case 'lg':
                    baseClasses.push('ppv2-py-3', 'ppv2-text-lg')
                    break
                default:
                    baseClasses.push('ppv2-py-2')
            }

            if (this.error) {
                baseClasses.push('ppv2-border-red-300', 'ppv2-focus:ring-red-500', 'ppv2-focus:border-red-500')
            }

            if (this.disabled) {
                baseClasses.push('ppv2-bg-gray-50', 'ppv2-text-gray-500', 'ppv2-cursor-not-allowed')
            } else {
                baseClasses.push('ppv2-bg-white')
            }

            return baseClasses.join(' ')
        },
    },
    methods: {
        onInput(event) {
            let value = event.target.value
            if (this.type === 'number') {
                value = value !== '' ? Number(value) : null
            }
            this.$emit('input', value)
        },
    },
}
</script>

<style scoped>
.border {
    border: 1px solid #7b82894d;
}
</style>
